import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { api } from "@/lib/api";
import { FALLBACK_TERMS_BODY, FALLBACK_TERMS_EFFECTIVE_DATE, FALLBACK_TERMS_VERSION } from "@/lib/legalTerms";
import { Button } from "./Primitives";
import { Spinner } from "./Spinner";

export function TermsModal({ visible, onClose, onAgree, showAgreeCta = true }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    let alive = true;
    (async () => {
      setLoading(true); setErrored(false);
      try { const r = await api("/legal/terms", { auth: false }); if (alive) setPayload(r); }
      catch { if (alive) setErrored(true); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [visible]);

  const resolved = useMemo(() => payload || { version: FALLBACK_TERMS_VERSION, effective_date: FALLBACK_TERMS_EFFECTIVE_DATE, body: FALLBACK_TERMS_BODY }, [payload]);

  if (!visible) return null;
  return (
    <div className="fade-in fixed inset-0 z-[8500] flex items-stretch justify-center bg-black/50 sm:items-center sm:p-6">
      <div className="sheet-in flex h-full w-full max-w-2xl flex-col overflow-hidden bg-a-bg sm:h-[88vh] sm:rounded-2xl sm:border sm:border-a-border" data-testid="terms-modal">
        <div className="flex items-center border-b border-a-border bg-a-surface px-4 py-3.5">
          <div className="flex-1 pr-3">
            <p className="text-base font-extrabold text-a-text">Terms &amp; Conditions and Privacy</p>
            <p className="mt-0.5 text-[11px] text-a-muted">Version {resolved.version} · Effective {resolved.effective_date}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-a-text" data-testid="terms-close"><X size={22} /></button>
        </div>
        {loading ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2"><Spinner /><p className="text-a-muted">Loading agreement…</p></div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 pb-10" data-testid="terms-scroll">
            {errored ? (
              <div className="mb-3.5 flex items-center gap-1.5 rounded-[10px] border border-a-warning bg-a-warning/10 p-2.5 text-xs text-a-warning">
                <AlertTriangle size={16} /> Couldn&apos;t reach the server — showing offline copy of the agreement.
              </div>
            ) : null}
            <p className="whitespace-pre-wrap text-[13px] leading-5 text-a-text">{resolved.body}</p>
          </div>
        )}
        <div className="space-y-1.5 border-t border-a-border bg-a-surface p-3.5">
          {showAgreeCta ? (
            <>
              <Button testID="terms-agree-cta" label="I Agree & Continue" size="lg" onPress={() => { onAgree?.(resolved.version); onClose(); }} />
              <button onClick={onClose} className="w-full py-2.5 text-[13px] font-semibold text-a-muted" data-testid="terms-dismiss">Close without agreeing</button>
            </>
          ) : (
            <Button testID="terms-close-cta" label="Close" variant="secondary" size="lg" onPress={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}
